import React, { useState, useEffect } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { makeStyles, colors,  Button, Grid, AppBar } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import { appProfileSelector } from 'redux/selectors';
import { setLoading, updateApp } from 'redux/actions/app';
import * as API from 'services/api';
import {
  ProfileContainer,
  ProfileBasic,
  PasswordSection,
  AdditionalInfo,
  CompanyInfo,
  Services,
  Notifications,
  Orders,
  Users
} from './components';
import { isPhone } from 'utils/validations';
import { TabPanel, StyledTabs, StyledTab } from 'components/CustomTab'
import { parseURLQuery } from 'utils';
import PropTypes from 'prop-types';
import { showMessage } from 'utils';

const useStyles = makeStyles(theme => ({
  button: {
    marginTop: theme.spacing(2),
    display: 'block',
    borderRadius: 4
  },
  divider: {
    margin: '20px 0',
    backgroundColor: colors.grey[400],
  },
  rightSpacing: {
    paddingTop: 100 + theme.spacing(3),
  },
  appbar: {
    boxShadow: 'none',
    marginBottom: theme.spacing(1)
  },
  tabsContainer: {
    backgroundColor: '#F2F5FB',
    color: theme.palette.app.primary,
    // allow tabs to wrap on small screens so buttons drop to next line
    '& .MuiTabs-flexContainer': {
      [theme.breakpoints.down('sm')]: {
        flexWrap: 'wrap',
        justifyContent: 'center',
      },
    },
    // make the tab buttons smaller on small screens
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1, 0),
      '& button': {
        margin: theme.spacing(0.5, 1),
        padding: theme.spacing(0.5, 2),
        fontSize: theme.typography.pxToRem(13),
        minWidth: 0,
      },
    }
  },
  tabContainer: {

  }
}));

const ProfileSettings = ({ intl: { formatMessage }, location }) => {
  const classes = useStyles();
  const queries = location?.search ? parseURLQuery(location.search) : null
  const [value, setValue] = useState(queries?.tab ? queries.tab : "myprofile");
  const profile = useSelector(appProfileSelector);
  const [agency, setAgency] = useState();

  const [formState, setFormState] = useState({
    firstName: profile.firstName,
    lastName: profile.lastName,
    position: profile.position,
    picture: profile.picture,
    email: profile.email,
    phone: profile.phone,
    address: profile.address,
    city: profile.city,
    country: profile.country,
    zipCode: profile.zipCode,
    company: profile.company,
    notification_newdoc: profile.notification_newdoc,
    notification_newsession: profile.notification_newsession,
    notification_newreport: profile.notification_newreport,
    notification_newoffre: profile.notification_newoffre,
    logo: profile.logo,
    oldPassword: '',
    newPassword: '',
    newPasswordConfirm: '',
    file: null, // avatar
    fileSrc: '',
    logoFile: null, // logo
    logoFileSrc: '',
  });
  const [apaves, setApaves] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setLoading(true));
    API.listApaveApps()
      .then(res => {
        dispatch(setLoading(false));
        setApaves(res.data);
      })
      .catch(() => {
        dispatch(setLoading(false));
      });


    API.getAgenciesWithCodepostal({ codePostal: profile.zipCode }).then(res => {
      if (res.code == 200) {
        setAgency(res.data[0])
      }
    })

  }, []);
  const handleChangeTab = (event, newValue) => {
    setValue(newValue);

    window.dataLayer.push({
      'event': 'navigation',
      'navigation_template': 'Profile',
      'navigation_path': window.location.href,
      'click_name': formatMessage({ id: "profile.tab."+newValue })
    });
  };
  const handleChange = event => {
    if (event.persist) {
      event.persist();
    }
    setFormState(formState => ({
      ...formState,
      [event.target.name]:
        event.target.type === 'checkbox'
          ? event.target.checked
          : event.target.value,
    }));
  };

  const onUpdate = () => {
    // Frontend validation: Check if new password and confirm password match
    if (formState.newPassword && formState.newPasswordConfirm) {
      if (formState.newPassword !== formState.newPasswordConfirm) {
        showMessage({
          message: formatMessage({ id: 'home.profile.password_mismatch' }) || 'New password and confirm password do not match',
          type: 'danger',
        });
        return;
      }
    }

    // If newPassword is provided, newPasswordConfirm must also be provided
    if (formState.newPassword && !formState.newPasswordConfirm) {
      showMessage({
        message: formatMessage({ id: 'home.profile.password_confirm_required' }) || 'Please confirm your new password',
        type: 'danger',
      });
      return;
    }

    // If newPasswordConfirm is provided, newPassword must also be provided
    if (formState.newPasswordConfirm && !formState.newPassword) {
      showMessage({
        message: formatMessage({ id: 'home.profile.password_required' }) || 'Please enter your new password',
        type: 'danger',
      });
      return;
    }

    dispatch(setLoading(true));
    const formData = new FormData();
    [
      'firstName',
      'lastName',
      'position',
      'phone',
      'address',
      'city',
      'country',
      'zipCode',
      'company',
      'notification_newdoc',
      'notification_newsession',
      'notification_newreport',
      'notification_newoffre'
    ].forEach(key => {
      formData.append(key, formState[key]);
    });

    if (formState.oldPassword) {
      formData.append('oldPassword', formState.oldPassword);
    }
    if (formState.newPassword) {
      formData.append('password', formState.newPassword);
      // Also send confirmPassword for backend validation
      formData.append('confirmPassword', formState.newPasswordConfirm);
    }

    if (formState.file !== null) {
      formData.append('picture', formState.file);
    }

    if (formState.logoFile !== null) {
      formData.append('logo', formState.logoFile);
    }

    API.updateProfile(profile.id, formData)
      .then(res => {
        dispatch(setLoading(false));
        if (res.code === 200) {
          showMessage({
            message: 'Profile is updated successfully!',
            type: 'success',
          });
          dispatch(
            updateApp({
              profile: res.user,
            }),
          );
          setFormState(formState => ({
            ...formState,
            oldPassword: '',
            newPassword: '',
            newPasswordConfirm: '',
            file: null,
            fileSrc: '',
            picture: res.user.picture,
            logoFile: null,
            logoFileSrc: '',
            logo: res.user.logo,
          }));
        } else {
          showMessage({
            message: res.message,
            type: 'danger',
          });
        }
      })
      .catch(err => {
        console.log(err)
        dispatch(setLoading(false));
        setFormState(formState => ({
          ...formState,
          errorMessage: err.message,
        }));
      });
  };

  const buttonDisabled =
    [
      'firstName',
      'lastName',
      'position',
      'address',
      'city',
      'country',
      'zipCode',
      'company',
    ]
      .map(
        e =>
          e !== 'phone'
            ? formState[e] === ''
            : formState[e] === '' ||
            (formState[e] !== '' && !isPhone(formState[e])),
      )
      .reduce((e1, e2) => e1 || e2, false) ||
    ([
      'firstName',
      'lastName',
      'position',
      'phone',
      'address',
      'company',
      'city',
      'country',
      'zipCode',
      'notification_newdoc',
      'notification_newsession',
      'notification_newreport',
      'notification_newoffre'
    ]
      .map(key => formState[key] === profile[key])
      .reduce((e1, e2) => e1 && e2, true) &&
      formState.logoFile === null &&
      formState.file === null &&
      formState.newPassword === '' &&
      formState.newPasswordConfirm === '');

  return (
    <ProfileContainer>
      <AppBar position="static" className={classes.appbar}>
        <StyledTabs value={value} onChange={handleChangeTab} aria-label="profiles tabs" className={classes.tabsContainer}>
          <StyledTab value="myprofile" className={classes.tabContainer} label={<FormattedMessage id="profile.tab.myprofile" />} />
          {(profile.role == "client" || profile.role == "admin") && <StyledTab value="users" className={classes.tabContainer} label={<FormattedMessage id="profile.tab.users" />} />}
          <StyledTab value="services" className={classes.tabContainer} label={<FormattedMessage id="profile.tab.services" />} />
          <StyledTab value="orders" className={classes.tabContainer} label={<FormattedMessage id="profile.tab.orders" />} />
          <StyledTab value="contacts" className={classes.tabContainer} label={<FormattedMessage id="profile.tab.contacts" />} />
          <StyledTab value="notifications" className={classes.tabContainer} label={<FormattedMessage id="profile.tab.notifications" />} />
        </StyledTabs >
      </AppBar>

      <TabPanel value={value} index={"myprofile"} style={{marginLeft : 20, marginRight : 20}}>
        <Grid container spacing={4}>
          <ProfileBasic
            data={formState}
            handleChange={handleChange}
            setFormState={setFormState}
            email={profile.email}
            formState={formState}
          />
          <CompanyInfo
            data={formState}
            handleChange={handleChange}
            setFormState={setFormState}
            disabled={profile.role != "client"}
          />
          <Grid item xs={12} sm={6}></Grid>
          <Grid item xs={12} sm={6}>
            <PasswordSection data={formState} handleChange={handleChange} onSave={onUpdate} disabled={buttonDisabled} />
          </Grid>
        </Grid>
        <Button
          variant="contained"
          className={classes.button}
          onClick={onUpdate}
          disabled={buttonDisabled}
        >
          <FormattedMessage id="save" />
        </Button>
      </TabPanel>
      <TabPanel value={value} index={"users"} style={{marginLeft : 20, marginRight : 20}}>
        <Users />
      </TabPanel>
      <TabPanel value={value} index={"services"} style={{marginLeft : 20, marginRight : 20}}>
        <Services apaves={apaves} data={profile} />
      </TabPanel>
      <TabPanel value={value} index={"orders"} style={{marginLeft : 20, marginRight : 20}}>
        <Orders apaves={apaves} data={profile} />
      </TabPanel>
      <TabPanel value={value} index={"contacts"} style={{marginLeft : 20, marginRight : 20}}>
        <AdditionalInfo data={formState} agencyData={agency}/>
      </TabPanel>
      <TabPanel value={value} index={"notifications"} style={{marginLeft : 20, marginRight : 20}}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <Notifications data={formState} handleChange={handleChange} onSave={onUpdate} disabled={buttonDisabled} />
          </Grid>
        </Grid>
      </TabPanel>
    </ProfileContainer>
  );
};
ProfileSettings.propTypes = {
  location: PropTypes.any
};
export default injectIntl(ProfileSettings);
